import { Button, Checkbox, Dialog, TextInput, Tooltip, Typography } from '@neo4j-ndl/react';
import { OPS_EVENTS } from '@nx/analytics-service';
import type { Instance } from '@nx/state';
import React, { useEffect, useMemo, useState } from 'react';

import { track } from '../../../services/segment/analytics';
import { isLiveMigration, kindOfMigration } from './common';

interface Props {
  onAccept: (instanceToDelete: Instance) => void;
  onDecline: () => void;
  originalInstance: Instance;
  migratedInstance: Instance;
  open: boolean;
}

export const FinalizeMigrationDialog = ({ onAccept, onDecline, originalInstance, migratedInstance, open }: Props) => {
  const [confirmed, setConfirmed] = useState(false);
  const [inputInstanceName, setInputInstanceName] = useState('');

  const trackOnDecline: () => void = () => {
    if (isLiveMigration(migratedInstance)) {
      track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CANCEL_LIVE_MIGRATION_DIALOG);
    } else {
      track(OPS_EVENTS.MIGRATION_READINESS_REPORT_CANCEL_TEST_MIGRATION_DIALOG);
    }
    onDecline();
  };

  const handleTextInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputInstanceName(event.target.value);
  };

  const handleConfirmedChange = (checked: boolean) => {
    setConfirmed(checked);
  };

  const deleteLabel = useMemo(() => {
    if (isLiveMigration(migratedInstance)) {
      return 'During finalization the version 4 instance will be deleted';
    }
    return 'During finalization the test migration instance will be deleted';
  }, [migratedInstance]);

  const instanceToDelete = useMemo(() => {
    if (isLiveMigration(migratedInstance)) {
      return originalInstance;
    }
    return migratedInstance;
  }, [migratedInstance, originalInstance]);

  const canEnableFinalizeButton = useMemo(() => {
    if (!confirmed) {
      return false;
    }
    return inputInstanceName === instanceToDelete.name;
  }, [confirmed, inputInstanceName, instanceToDelete.name]);

  const [tipOpen, setTipOpen] = useState(false);
  useEffect(() => {
    if (tipOpen) {
      setTimeout(() => {
        setTipOpen(false);
      }, 2000);
    }
  }, [tipOpen]);

  const [isFinalizing, setIsFinalizing] = useState(false);
  return (
    <>
      <Dialog isOpen={open} onClose={trackOnDecline}>
        <Dialog.Header>
          Finalize {kindOfMigration(migratedInstance)} migration -{' '}
          <Tooltip isOpen={tipOpen} type="simple" placement="right" isPortaled={false}>
            <Tooltip.Trigger
              className="cursor-text"
              htmlAttributes={{
                onClick: () => {
                  void navigator.clipboard.writeText(instanceToDelete.name);
                  if (!tipOpen) {
                    setTipOpen(true);
                  }
                },
                'aria-label': 'Copy text',
              }}
            >
              {instanceToDelete.name}
            </Tooltip.Trigger>
            <Tooltip.Content className="flex items-center">Copied to clipboard</Tooltip.Content>
          </Tooltip>
        </Dialog.Header>
        <Dialog.Content className="space-y-6">
          <h5>Acknowledge finalization</h5>
          <Checkbox
            label={
              kindOfMigration(migratedInstance) === 'live'
                ? `I have completed the migration to the new instance.`
                : `I have completed testing my application on the ${kindOfMigration(migratedInstance)} migration instance.`
            }
            onChange={(event) => handleConfirmedChange(event.target.checked)}
            isDisabled={false}
            isChecked={confirmed}
          />
          <div className="ndl-divider ndl-divider-horizontal gap-4 pt-4" role="separator" aria-orientation="horizontal">
            <div>{deleteLabel}</div>
            <Typography variant="body-small">
              This action is irreversible. We will delete your instance and all associated snapshots.
            </Typography>
            <TextInput
              className="pt-2"
              label="Type the name of the instance below to confirm deletion."
              onChange={handleTextInput}
            />
          </div>
        </Dialog.Content>
        <Dialog.Actions>
          <Button onClick={trackOnDecline} color="neutral" fill="outlined" size="medium">
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (isLiveMigration(migratedInstance)) {
                track(OPS_EVENTS.MIGRATION_READINESS_REPORT_FINISH_LIVE_MIGRATION);
              } else {
                track(OPS_EVENTS.MIGRATION_READINESS_REPORT_FINISH_TEST_MIGRATION);
              }
              setIsFinalizing(true);
              onAccept(instanceToDelete);
            }}
            isLoading={isFinalizing}
            color="primary"
            fill="filled"
            size="medium"
            isDisabled={!canEnableFinalizeButton || isFinalizing}
          >
            Finalize {kindOfMigration(migratedInstance)} migration
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};
